import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Building2,
  Smartphone,
  ShoppingCart,
  Link as LinkIcon,
  BarChart3,
  Code2,
  RotateCw,
  Trophy,
  Gem,
  CheckCircle2,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

/* ─────────────────────────────── assets ─────────────────────────────── */
const CEO_PHOTO   = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/bkx1v4ho_CEO%20PICTURE.png';
const FCA_BADGE   = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/jzv5pm0i_FCA%20BADGE.png';
const PCI_BADGE   = 'https://customer-assets.emergentagent.com/job_6323abd7-24b9-4750-94c8-d1bc2ce3dd11/artifacts/sv4k3rhu_IM6.png';
const CLIENT_LDS  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/1dekfa4l_MPM%20IMAGE.png';
const CLIENT_TRA  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/2i32eq05_USE%202.png';
const CLIENT_MON  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/c1bhuzuq_USE%203.png';
const CLIENT_KEAT = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/uu5ym1es_ACRES%20IMAGE.png';
const CLIENT_ALFC = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/uuuoh4mc_SALT%20CAVE%20IMAGE.png';
const CLIENT_HRI  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/pfwt3g1s_HRI%20IMAGE.png';
const AWARD_OPEX  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/a66cffus_USE%204.png';
const AWARD_IT    = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/m61gqvsf_USE%205.png';
const AWARD_DEL   = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/r6gphsak_USE%206.png';
const AWARD_ECOM  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/vqv532a4_AWARD%204.jpg';

/* ─────────────────────────────── data ─────────────────────────────── */
const WHAT_WE_DELIVER = [
  { icon: Building2,   title: 'Payment Terminals',         description: 'Countertop, portable and mobile payment terminals.' },
  { icon: Smartphone,  title: 'MPOS & Tap-on-Phone',       description: 'Accept payments anywhere using just a smartphone.' },
  { icon: ShoppingCart,title: 'eCommerce Gateway',         description: 'Hosted checkout services and seamless online integration.' },
  { icon: LinkIcon,    title: 'Pay By Link & Remote',      description: 'Generate secure payment links for remote transactions.', featured: true },
  { icon: RotateCw,    title: 'Recurring & Tokenisation',  description: 'Automated recurring billing and secure tokenisation services.' },
  { icon: BarChart3,   title: 'Reporting & Analytics',     description: 'Comprehensive merchant reporting and business insights.' },
  { icon: Code2,       title: 'API & Developer Tools',     description: 'Flexible integrations and developer-friendly APIs.' },
];

const CLIENTS = [
  { logo: CLIENT_ALFC, name: 'Salt Cave Paradise',  large: true },
  { logo: CLIENT_LDS,  name: 'MPM Event Services',  huge: true },
  { logo: CLIENT_HRI,  name: 'Horse Racing Ireland' },
  { logo: CLIENT_TRA,  name: 'Tramore Golf Club',   bigger: true },
  { logo: CLIENT_MON,  name: 'Mondello Park',       bigger: true },
  { logo: CLIENT_KEAT, name: 'The Acres',           medium: true },
];

const AWARDS = [
  { logo: AWARD_OPEX, name: 'Operational Excellence Awards', year: '2021', caption: 'Winner — recognising operational excellence in payments.' },
  { logo: AWARD_IT,   name: 'Irish Times Innovation Awards', year: '2020', caption: 'IT & Fintech Category Winner — recognising innovative thinking in business.' },
  { logo: AWARD_DEL,  name: 'Deloitte Technology Fast 50',   year: '2021', caption: "Winner — Ireland's fastest-growing technology companies." },
  { logo: AWARD_ECOM, name: 'Best Payments Startup',         year: 'Winner', caption: 'eCommerce & Payment Industry Awards — recognising innovation in payments.' },
];

/* ─────────────────────────────── small components ─────────────────────────────── */
const Eyebrow = ({ children, testId }) => (
  <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3" data-testid={testId}>{children}</p>
);

const SectionHeading = ({ eyebrow, title, accent = true, light = false, testId }) => (
  <div className="mb-8" data-testid={testId}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-tight tracking-tight ${light ? 'text-white' : 'text-[#0a1929]'}`}>{title}</h2>
    {accent && <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />}
  </div>
);

/* ─────────────────────────────── page ─────────────────────────────── */
const AboutCompany = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="about-company-page">
      <Header />
      <div className="h-20" />

      {/* ╔═════════════════════ HERO ═════════════════════╗ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f7f9fc] via-white to-[#eef3f8]" data-testid="company-hero">
        {/* Soft background orbs */}
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#1F4E79]/8 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-[#ef5b25]/8 blur-3xl" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 sm:pt-16 sm:pb-24">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1F4E79] font-medium mb-10 transition-colors duration-200"
            data-testid="company-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-white/80 via-white/95 to-white/80 backdrop-blur-md border border-white/80 text-[#1F4E79] text-[11px] font-bold uppercase tracking-[0.22em] mb-6 shadow-[0_4px_20px_-2px_rgba(31,78,121,0.18)] ring-1 ring-inset ring-white/60"
                data-testid="company-hero-chip"
              >
                <Gem size={13} className="text-[#ef5b25]" strokeWidth={2} />
                About eCOMM
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-bold text-[#0a1929] leading-[1.05] tracking-tight mb-6" data-testid="company-title">
                Payments Technology<br />
                <span className="text-[#1F4E79]">Designed For</span> Modern Business
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mb-10 font-medium">
                eCOMM Merchant Solutions provides secure, scalable payment technologies that help businesses accept, manage and optimise payments across online, mobile and in-person channels.
              </p>
              <div className="flex">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="company-cta-contact"
                >
                  Speak to our Team</Link>
              </div>
            </div>

            {/* Hero feature card — single Award-Winning Innovation card */}
            <div className="lg:col-span-5">
              <div
                className="relative rounded-2xl bg-[#0a1929]/85 backdrop-blur-xl border border-white/10 text-white shadow-[0_20px_50px_-12px_rgba(10,25,41,0.45)] p-8 sm:p-10 overflow-hidden"
                data-testid="hero-stat-3"
              >
                <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#ef5b25]/30 blur-3xl" aria-hidden="true" />
                <div className="pointer-events-none absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-[#1F4E79]/40 blur-3xl" aria-hidden="true" />
                <div className="relative">
                  <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-3">Award-Winning Innovation</p>
                  <p className="font-bold text-white text-2xl sm:text-[1.65rem] leading-snug mb-5">Recognised for Excellence in Payments &amp; Fintech Innovation.</p>
                  <div className="h-px w-12 bg-[#ef5b25]/60 mb-4" />
                  <p className="text-white/85 text-lg font-semibold tracking-wide">Gateway. Processing. Acquiring.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ WHO WE ARE ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="who-we-are">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Who We Are" title="An Integrated Commerce Partner." />
          </div>
          <div className="lg:col-span-7 space-y-5 text-gray-700 leading-relaxed text-base sm:text-lg">
            <p>
              eCOMM Merchant Solutions is a payment technology provider delivering integrated commerce solutions for businesses across multiple industries. From card payment terminals and eCommerce gateways to mobile payments and advanced reporting tools, we help merchants simplify payment acceptance through secure and reliable technology.
            </p>
            <p>
              Our solutions are designed to support businesses of all sizes — from independent retailers and hospitality operators to growing multi-location enterprises. We provide flexible payment tools tailored to modern commercial needs.
            </p>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ OUR ADVANTAGE ═════════════════════╗ */}
      <section className="bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] py-20 sm:py-24 relative overflow-hidden" data-testid="our-advantage">
        <div className="pointer-events-none absolute -top-32 right-0 w-96 h-96 rounded-full bg-[#ef5b25]/10 blur-3xl" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.22em] mb-3">Our Advantage</p>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-white leading-tight tracking-tight mb-4">A Unified Payments Ecosystem.</h2>
            <div className="h-1 w-20 bg-[#ef5b25] rounded-full mb-6" />
            <p className="text-lg text-white/90 leading-relaxed">
              eCOMM combines payment gateway technology, transaction processing and acquiring capabilities within a single unified ecosystem.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { title: 'Faster Processing',     body: 'A single in-house stack means transactions move quicker, with fewer intermediaries.' },
              { title: 'Operational Efficiency', body: 'Streamlined reconciliation, reporting and support — all under one roof.' },
              { title: 'Flexible Solutions',    body: 'Modular technology that adapts to modern business requirements at every scale.' },
            ].map((c, i) => (
              <div key={i} className="rounded-xl bg-white/10 backdrop-blur-md border border-white/15 p-6 hover:bg-white/15 transition-all duration-200" data-testid={`advantage-card-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#ef5b25]/20 border border-[#ef5b25]/30 flex items-center justify-center mb-4">
                  <CheckCircle2 size={20} className="text-[#ffcba6]" strokeWidth={2} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2 leading-tight">{c.title}</h3>
                <p className="text-white/80 text-[14px] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>

          <p className="text-white/80 text-base leading-relaxed mt-10 max-w-4xl">
            Our technology-first approach focuses on security, reliability and innovation — helping merchants simplify payment acceptance across in-store, online and mobile channels.
          </p>
        </div>
      </section>

      {/* ╔═════════════════════ WHAT WE DELIVER ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="what-we-deliver">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionHeading eyebrow="What We Deliver" title="A Comprehensive Payments Suite." />
            <p className="text-lg text-gray-700 leading-relaxed">
              eCOMM provides a comprehensive suite of payment and business management solutions designed to support modern commerce — combining payment acceptance with operational insight.
            </p>
          </div>

          {/* Layout: 3 boxes top + 3 boxes bottom (cols 1-3), Pay By Link spans rows on col 4 side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch" data-testid="deliver-grid">
            {WHAT_WE_DELIVER.map((item, i) => {
              const Icon = item.icon;
              const featured = item.featured;
              return (
                <div
                  key={i}
                  className={`group relative rounded-2xl ${
                    featured
                      ? 'bg-gradient-to-br from-[#1F4E79] via-[#163a5f] to-[#0a1929] text-white shadow-[0_20px_50px_-12px_rgba(31,78,121,0.45)] hover:shadow-[0_28px_60px_-12px_rgba(31,78,121,0.55)] lg:col-start-4 lg:row-start-1 lg:row-span-2 lg:self-center lg:h-fit'
                      : 'bg-gradient-to-br from-white via-white to-[#f0f5fa] border border-gray-100 shadow-[0_10px_30px_-8px_rgba(31,78,121,0.18)] hover:shadow-[0_18px_38px_-8px_rgba(31,78,121,0.28)] hover:border-[#1F4E79]/25'
                  } p-6 hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col`}
                  data-testid={`deliver-card-${i}`}
                >
                  {featured && (
                    <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#ef5b25]/30 blur-3xl" aria-hidden="true" />
                  )}
                  <div className={`relative w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    featured
                      ? 'bg-white/15 border border-white/25'
                      : 'bg-gradient-to-br from-[#1F4E79]/10 to-[#1F4E79]/5 group-hover:from-[#1F4E79]/15 group-hover:to-[#1F4E79]/10'
                  } transition-colors duration-200`}>
                    <Icon size={22} className={featured ? 'text-[#ffcba6]' : 'text-[#1F4E79]'} strokeWidth={1.75} />
                  </div>
                  <h3 className={`relative font-bold text-[15px] leading-snug mb-1.5 ${featured ? 'text-white' : 'text-[#0a1929]'}`}>{item.title}</h3>
                  <p className={`relative text-[13.5px] leading-relaxed ${featured ? 'text-white/85' : 'text-gray-600'}`}>{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ OUR APPROACH ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="our-approach">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-br from-[#f7f9fc] to-[#eef3f8] border border-gray-100 p-8 sm:p-12 lg:p-14 relative overflow-hidden">
            <div className="pointer-events-none absolute top-0 right-0 w-72 h-72 rounded-full bg-[#1F4E79]/8 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <Eyebrow>Our Approach</Eyebrow>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-[#0a1929] leading-tight tracking-tight mb-6">
                Practical, secure technology that<br className="hidden sm:block" /> simplifies how merchants get paid.
              </h2>
              <div className="h-1 w-20 bg-[#ef5b25] rounded-full mb-6" />
              <p className="text-lg text-gray-700 leading-relaxed mb-5">
                We believe payment technology should be secure, reliable and easy to use. Our approach focuses on delivering practical solutions that simplify operations, improve customer experiences and support long-term business growth.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                By working closely with merchants and partners, we continue to develop flexible solutions that adapt to changing customer expectations and evolving payment technologies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ SECURITY & RELIABILITY ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="security-reliability">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Committed To Security & Reliability" title="Trusted. Regulated. Compliant." />
            <p className="text-lg text-gray-700 leading-relaxed mb-5">
              Security and compliance remain central to eCOMM's payment infrastructure and operational processes. Our platform is built to support secure transaction processing and trusted payment acceptance across multiple channels.
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              eCOMM Merchant Solutions Limited is authorised by the Financial Conduct Authority (FCA) in the United Kingdom under licence number <strong className="text-[#1F4E79]">900591</strong>.
            </p>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-5" data-testid="compliance-badges">
            <div className="rounded-xl bg-white border border-gray-100 shadow-md p-5 flex flex-col items-center justify-center text-center h-52">
              <div className="flex-1 flex items-center justify-center w-full">
                <img src={FCA_BADGE} alt="FCA — Financial Conduct Authority" className="max-h-[140px] max-w-full object-contain" />
              </div>
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-[0.12em] mt-2">FCA Authorised</p>
            </div>
            <div className="rounded-xl bg-white border border-gray-100 shadow-md p-5 flex flex-col items-center justify-center text-center h-52">
              <div className="flex-1 flex items-center justify-center w-full">
                <img src={PCI_BADGE} alt="PCI DSS Compliant" className="max-h-[150px] max-w-[90%] object-contain" />
              </div>
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-[0.12em] mt-2">PCI DSS Compliant</p>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ TRANSPARENCY & CUSTOMER PROTECTION ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="transparency-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionHeading
              eyebrow="Transparency & Customer Protection"
              title="Customer Care · Legal & Regulatory"
            />
            <p className="text-lg text-gray-700 leading-relaxed">
              At eCOMM Merchant Solutions, maintaining the highest standards of security,
              compliance and customer care is fundamental to everything we do. Our policies and
              regulatory statements are designed to provide clarity, protect customer information
              and ensure a fair and transparent experience for all merchants.
            </p>
          </div>

          {/* 3 + 2 centered layout via 6-col trick on lg */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6" data-testid="legal-tiles">
            {[
              {
                title: 'SCA Statement',
                desc: 'Learn how eCOMM supports Strong Customer Authentication requirements to help reduce fraud and ensure secure payment experiences.',
                to: '/sca-statement',
                testId: 'legal-tile-sca',
                lgPos: 'lg:col-span-2 lg:col-start-1',
              },
              {
                title: 'Privacy Policy',
                desc: 'Understand how we collect, process and protect personal information in accordance with applicable data protection regulations.',
                to: '/privacy-policy',
                testId: 'legal-tile-privacy',
                lgPos: 'lg:col-span-2 lg:col-start-3',
              },
              {
                title: 'Terms of Service',
                desc: 'Review the terms governing the use of eCOMM products and services, including merchant responsibilities and service conditions.',
                to: '/terms-of-service',
                testId: 'legal-tile-terms',
                lgPos: 'lg:col-span-2 lg:col-start-5',
              },
              {
                title: 'Cookie Policy',
                desc: 'Learn how cookies are used across our website to enhance functionality, improve performance and personalise your experience.',
                to: '/cookies-policy',
                testId: 'legal-tile-cookies',
                lgPos: 'lg:col-span-2 lg:col-start-2',
              },
              {
                title: 'Complaints & Resolution',
                desc: 'Find out how to raise a concern, what to expect from our complaints process and the options available should further escalation be required.',
                to: '/complaints-resolution',
                testId: 'legal-tile-complaints',
                lgPos: 'lg:col-span-2 lg:col-start-4',
              },
            ].map((tile) => (
              <Link
                key={tile.to}
                to={tile.to}
                data-testid={tile.testId}
                className={`group relative flex flex-col rounded-xl bg-white border border-gray-100 p-7 hover:shadow-xl hover:border-[#1F4E79]/20 transition-all duration-200 ${tile.lgPos}`}
              >
                <div className="absolute top-5 right-5 text-gray-300 group-hover:text-[#ef5b25] group-hover:translate-x-1 transition-all duration-200">
                  <ChevronRight size={22} strokeWidth={2.25} />
                </div>
                <h3 className="font-bold text-[#0a1929] text-lg leading-tight mb-3 pr-8 group-hover:text-[#1F4E79] transition-colors">
                  {tile.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{tile.desc}</p>
                <span className="mt-5 inline-flex items-center text-xs font-semibold text-[#1F4E79] group-hover:text-[#ef5b25] transition-colors uppercase tracking-[0.12em]">
                  Read more
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* ╔═════════════════════ AWARDS ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="awards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionHeading eyebrow="Awards & Recognition" title="Recognised For Excellence." />
            <p className="text-lg text-gray-700 leading-relaxed">
              eCOMM has been recognised for innovation, operational excellence and growth within the payments and technology sector — reflecting our commitment to delivering secure, scalable and forward-thinking payment solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {AWARDS.map((award, i) => (
              <div
                key={i}
                className="group rounded-xl bg-gradient-to-br from-[#f7f9fc] to-white border border-gray-100 p-6 sm:p-8 hover:shadow-xl hover:border-[#ef5b25]/30 transition-all duration-200 flex flex-col sm:flex-row gap-5 items-center sm:items-start"
                data-testid={`award-card-${i}`}
              >
                <div className="flex-shrink-0 w-32 h-32 sm:w-36 sm:h-36 rounded-lg bg-white border border-gray-100 shadow-sm flex items-center justify-center p-3">
                  <img src={award.logo} alt={`${award.name} ${award.year}`} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#ef5b25]/10 border border-[#ef5b25]/20 text-[#ef5b25] text-[10px] font-bold uppercase tracking-[0.15em] mb-2.5">
                    <Trophy size={11} />
                    Winner · {award.year}
                  </div>
                  <h3 className="font-bold text-[#0a1929] text-xl leading-tight mb-2">{award.name}</h3>
                  <p className="text-gray-600 text-[14px] leading-relaxed">{award.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ TRUSTED BY (Clients) ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-16 sm:py-20" data-testid="trusted-by-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3">Trusted By</p>
          <h2 className="text-2xl sm:text-3xl lg:text-[2rem] font-bold text-[#0a1929] leading-tight tracking-tight mb-10">
            Powering Payments For Leading Brands
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-4 sm:gap-x-8 sm:gap-y-10 max-w-6xl mx-auto items-center">
            {CLIENTS.map((client, i) => (
              <div
                key={i}
                className={`flex items-center justify-center px-2 opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-200 ${
                  client.huge ? 'h-40 sm:h-64 lg:h-72'
                    : client.large ? 'h-36 sm:h-60 lg:h-[17rem]'
                    : client.bigger ? 'h-32 sm:h-52 lg:h-64'
                    : client.medium ? 'h-28 sm:h-44 lg:h-56'
                    : 'h-16 sm:h-24'
                }`}
                data-testid={`client-logo-${i}`}
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className={`max-w-full object-contain ${
                    client.huge ? 'max-h-[160px] sm:max-h-[240px] lg:max-h-[280px]'
                      : client.large ? 'max-h-[140px] sm:max-h-[210px] lg:max-h-[250px]'
                      : client.bigger ? 'max-h-[120px] sm:max-h-[180px] lg:max-h-[240px]'
                      : client.medium ? 'max-h-[100px] sm:max-h-[150px] lg:max-h-[200px]'
                      : 'max-h-[60px] sm:max-h-[90px]'
                  }`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ LEADERSHIP & VISION ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* CEO Portrait card */}
          <div className="lg:col-span-5">
            <div className="relative max-w-[280px] sm:max-w-xs lg:max-w-sm mx-auto lg:mx-0">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#1F4E79]/15 to-[#ef5b25]/15 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />
              <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#f7f9fc] to-[#eef3f8] border border-gray-100 shadow-2xl">
                <img src={CEO_PHOTO} alt="Noel Moran — CEO of eCOMM Merchant Solutions" className="w-full h-auto object-cover" data-testid="ceo-photo" />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0a1929]/90 via-[#0a1929]/60 to-transparent p-5 pt-14">
                  <p className="text-[10px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-1">Chief Executive Officer</p>
                  <p className="text-white font-bold text-xl leading-tight">Noel Moran</p>
                </div>
              </div>
            </div>
          </div>

          {/* Vision copy */}
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Leadership & Vision" title="Building the next generation of payments." />
            <p className="text-lg text-gray-700 leading-relaxed mb-5">
              Under the leadership of CEO <strong className="text-[#0a1929]">Noel Moran</strong>, eCOMM continues to invest in innovative payment technologies designed to support merchants in an evolving digital economy.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our focus remains on delivering secure, flexible and scalable payment solutions that simplify commerce for businesses of all sizes.
            </p>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ CTA STRIP ═════════════════════╗ */}
      <section id="contact" className="py-16 sm:py-20 bg-white" data-testid="company-final-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] p-8 sm:p-14 text-center shadow-xl relative overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ef5b25]/15 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <h3 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-white leading-tight tracking-tight mb-4">
                Speak to our Team
              </h3>
              <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Discover how eCOMM's payment technologies can support your business operations and growth objectives.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-white/90 hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="cta-final-contact"
                >
                  Contact Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default AboutCompany;
